import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Loader2 } from "lucide-react";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { useToast } from "@/components/ui/use-toast";

interface InactiveAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEmail: string;
}

function parseErrorMessage(err: any): string {
  const data = err?.response?.data;
  if (typeof data?.detail === "string" && data.detail.trim()) return data.detail;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof data?.error === "string" && data.error.trim()) return data.error;
  return "Unable to send activation email. Please try again.";
}

const InactiveAccountDialog: React.FC<InactiveAccountDialogProps> = ({
  open,
  onOpenChange,
  defaultEmail,
}) => {
  const { toast } = useToast();

  const [step, setStep] = useState<"confirm" | "email">("confirm");
  const [email, setEmail] = useState(defaultEmail);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const resetAndClose = () => {
    setStep("confirm");
    setEmail(defaultEmail);
    setError("");
    setLoading(false);
    onOpenChange(false);
  };

  const handleYes = () => {
    setEmail(defaultEmail);
    setStep("email");
  };

  const handleSend = async () => {
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await axiosInstance.post(endpoints.request_account_activation, { email });
      toast({
        title: "Activation email sent",
        description:
          res?.data?.message ??
          "Check your inbox for instructions to activate your account.",
      });
      resetAndClose();
    } catch (err: any) {
      setError(parseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetAndClose();
        else onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        {step === "confirm" ? (
          <>
            <DialogHeader>
              <DialogTitle>Activate Your Account</DialogTitle>
              <DialogDescription>
                Your account is currently inactive. Are you sure you want to activate it?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button type="button" variant="secondary" onClick={resetAndClose}>
                Cancel
              </Button>
              <Button type="button" onClick={handleYes}>
                Yes
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Confirm Your Email</DialogTitle>
              <DialogDescription>
                We'll send activation instructions to this email address.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="activate-email">Email Address</Label>
              <div className="relative">
                <Mail
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  size={18}
                />
                <Input
                  id="activate-email"
                  type="email"
                  placeholder="name@company.com"
                  className={`pl-10 h-11 ${error ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value.toLowerCase());
                    if (error) setError("");
                  }}
                />
              </div>
              {error && <p className="text-xs text-red-500">{error}</p>}
            </div>

            <DialogFooter className="flex flex-col gap-3 sm:flex-row sm:justify-end mt-3">
              <Button type="button" variant="secondary" onClick={resetAndClose} disabled={loading}>
                Cancel
              </Button>
              <Button type="button" onClick={handleSend} disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send"
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InactiveAccountDialog;